/**
 * The Review API, from the browser.
 *
 * Thin on purpose. Cloud's equivalent carries a lot of crypto plumbing because
 * every payload there is ciphertext; Review's payloads are plain, so this is
 * mostly `fetch` with one error shape.
 */

export interface ApiErrorBody {
  code: string;
  message: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { accept: 'application/json', ...(init.headers ?? {}) }
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: ApiErrorBody } | null;
    throw new ApiError(
      res.status,
      body?.error?.code ?? 'unknown',
      body?.error?.message ?? 'Something went wrong.'
    );
  }
  return (await res.json()) as T;
}

export interface ProjectTag {
  id: string;
  key: string;
  label: string;
  colour: string;
}

export interface ProjectWire {
  id: string;
  title: string;
  slug: string;
  state: 'importing' | 'live' | 'archived';
  round: number;
  dueAt: number | null;
  lessonCount: number;
  blockCount: number;
  fileCount: number;
  sizeBytes: number;
  /** Blocks whose media never made it into the archive. */
  missingMedia: string[];
  notifyCommentEmail: boolean;
  notifyDueEmail: boolean;
  createdAt: number;
  updatedAt: number;
  tags?: ProjectTag[];
}

export interface ReviewerWire {
  id: string;
  key: string;
  name: string;
  colour: string;
  /** Only sent to the project's owner. One reviewer does not need another's
   *  address, and on a public link "another reviewer" can be anyone. */
  email?: string;
  /** The author's entry — the one `u:` key in a list that is otherwise `r:`.
   *  Present so every client can attribute and @-mention the author. */
  isOwner?: boolean;
}

export interface AttachmentWire {
  id: string;
  mime: string;
  width: number | null;
  height: number | null;
}

export interface MessageWire {
  id: string;
  authorKey: string;
  body: string;
  /** Actor keys, resolved by the client that wrote the message. */
  mentions: string[];
  createdAt: number;
  editedAt: number | null;
  attachments: AttachmentWire[];
}

export interface ActivityWire {
  what: string;
  actorName: string;
  createdAt: number;
}

export interface ThreadWire {
  id: string;
  /** The round this was said in. Older than the project's current round and
   *  still open = carried over. */
  round: number;
  scope: 'block' | 'overall';
  lessonId: string | null;
  blockId: string | null;
  anchorLabel: string | null;
  tagId: string | null;
  /** An actor key (`u:…`/`r:…`), or null. A note, not a permission. */
  assignedTo: string | null;
  resolvedAt: number | null;
  /** The resolver's name as it stood at the time — display text, not a key. */
  resolvedBy: string | null;
  createdAt: number;
  reactions: number;
  reacted: boolean;
  activity: ActivityWire[];
  messages: MessageWire[];
}

export interface ThreadsWire {
  me: { key: string; name: string; isOwner: boolean; identified: boolean };
  project: { round: number; dueAt: number | null };
  /** The project's tag set, so the reviewer page can offer the picker. */
  tags: ProjectTag[];
  /** Current-round approvals. */
  approvals: Array<{ reviewerId: string; approvedAt: number }>;
  reviewers: ReviewerWire[];
  threads: ThreadWire[];
}

export interface NewThread {
  scope: 'block' | 'overall';
  body: string;
  blockId?: string;
  lessonId?: string;
  anchorLabel?: string;
  tagId?: string | null;
  mentions?: string[];
  attachmentIds?: string[];
}

const post = <T,>(path: string, body: unknown) =>
  request<T>(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body)
  });

export const api = {
  projects: {
    list: () => request<{ projects: ProjectWire[] }>('/api/review/projects'),
    get: (id: string) => request<ProjectWire>(`/api/review/projects/${id}`)
  },
  threads: {
    list: (projectId: string) =>
      request<ThreadsWire>(`/api/review/projects/${projectId}/threads`),
    create: (projectId: string, input: NewThread) =>
      post<{ id: string }>(`/api/review/projects/${projectId}/threads`, input),
    reply: (
      projectId: string,
      threadId: string,
      body: string,
      mentions: string[],
      attachmentIds: string[] = []
    ) =>
      post<{ ok: true }>(`/api/review/projects/${projectId}/threads/${threadId}/replies`, {
        body,
        mentions,
        attachmentIds
      }),
    resolve: (projectId: string, threadId: string, resolved: boolean) =>
      post<{ ok: true }>(`/api/review/projects/${projectId}/threads/${threadId}/resolve`, {
        resolved
      }),
    react: (projectId: string, threadId: string) =>
      post<{ reacted: boolean }>(`/api/review/projects/${projectId}/threads/${threadId}/react`, {}),
    setTag: (projectId: string, threadId: string, tagId: string | null) =>
      post<{ ok: true }>(`/api/review/projects/${projectId}/threads/${threadId}/tag`, { tagId }),
    assign: (projectId: string, threadId: string, assignee: string | null) =>
      post<{ ok: true }>(`/api/review/projects/${projectId}/threads/${threadId}/assign`, {
        assignee
      })
  },
  settings: {
    update: (
      projectId: string,
      input: { dueAt?: number | null; notifyCommentEmail?: boolean; notifyDueEmail?: boolean }
    ) =>
      request<{ ok: true }>(`/api/review/projects/${projectId}/settings`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input)
      })
  },
  pulse: (projectId: string) => request<PulseWire>(`/api/review/projects/${projectId}/pulse`),
  /** The author's signed course path on the preview origin. */
  preview: (projectId: string) =>
    request<{ path: string }>(`/api/review/projects/${projectId}/preview`),
  presence: (projectId: string) =>
    post<{ ok: true }>(`/api/review/projects/${projectId}/presence`, {}),
  approve: (projectId: string) =>
    post<{ approved: boolean }>(`/api/review/projects/${projectId}/approve`, {}),
  rounds: {
    list: (projectId: string) =>
      request<{ current: number; rounds: RoundWire[] }>(
        `/api/review/projects/${projectId}/rounds`
      ),
    diff: (projectId: string, from: number, to: number) =>
      request<DiffWire>(`/api/review/projects/${projectId}/rounds/diff?from=${from}&to=${to}`)
  },
  reviewers: {
    list: (projectId: string) =>
      request<ReviewerListWire>(`/api/review/projects/${projectId}/reviewers`)
  },
  invites: {
    create: (projectId: string, email: string) =>
      post<{ ok: true }>(`/api/review/projects/${projectId}/invites`, { email }),
    revoke: (projectId: string, inviteId: string) =>
      request<{ ok: true }>(`/api/review/projects/${projectId}/invites/${inviteId}`, {
        method: 'DELETE'
      })
  },
  notifications: {
    list: () =>
      request<{ unread: number; notifications: NotificationWire[] }>('/api/review/notifications'),
    /** One id marks one; none marks everything. */
    markRead: (id?: string) => post<{ ok: true }>('/api/review/notifications/read', { id })
  },
  tags: {
    replace: (projectId: string, tags: Array<{ label: string; colour: string }>) =>
      request<{ ok: true }>(`/api/review/projects/${projectId}/tags`, {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ tags })
      })
  },
  attachments: {
    /** Raw bytes; the server decides the type by sniffing them. */
    upload: async (projectId: string, file: File) => {
      const res = await fetch(`/api/review/projects/${projectId}/attachments`, {
        method: 'POST',
        body: file
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: ApiErrorBody } | null;
        throw new ApiError(
          res.status,
          body?.error?.code ?? 'unknown',
          body?.error?.message ?? 'That image would not upload.'
        );
      }
      return (await res.json()) as { id: string; mime: string; url: string };
    }
  },
  links: {
    get: (projectId: string) =>
      request<{ link: LinkSummary | null }>(`/api/review/projects/${projectId}/link`),
    /** The one response that carries the token. It is never returned again. */
    create: (projectId: string, input: { password?: string; confidential?: boolean }) =>
      post<{ url: string; token: string }>(`/api/review/projects/${projectId}/link`, input),
    revoke: (projectId: string) =>
      request<{ ok: true }>(`/api/review/projects/${projectId}/link`, { method: 'DELETE' }),
    resolve: (token: string, password?: string) =>
      post<Record<string, unknown>>('/api/review/public/links/resolve', { token, password }),
    identify: (token: string, name: string, email: string, password?: string) =>
      post<{ reviewerId: string; name: string }>('/api/review/public/links/identify', {
        token,
        name,
        email,
        password
      })
  }
};

export interface LinkSummary {
  id: string;
  hasPassword: boolean;
  confidential: boolean;
  expiresAt: number | null;
  viewCount: number;
}

export interface PulseWire {
  reviewersIn: number;
  unread: number;
  open: number;
  resolved: number;
  approvals: number;
}

export interface ReviewerListWire {
  reviewers: Array<{
    id: string;
    name: string;
    email: string;
    colour: string;
    lastSeenAt: number;
    commentedAt: number | null;
    comments: number;
    approvedAt: number | null;
    inviteId: string | null;
  }>;
  invites: Array<{ id: string; email: string; invitedAt: number; openedAt: number | null }>;
}

export interface RoundWire {
  round: number;
  dueAt: number | null;
  lessonCount: number;
  blockCount: number;
  openedAt: number;
  closedAt: number | null;
}

export interface BlockChangeWire {
  blockId: string;
  kind: 'edited' | 'added' | 'removed' | 'unchanged';
  lessonTitle: string;
  lessonIndex: number;
  blockKind: string;
  before: string | null;
  after: string | null;
  heading: string;
  answers: Array<{ threadId: string; who: string; round: number; resolved: boolean }>;
}

export interface DiffWire {
  from: number;
  to: number;
  stats: { edited: number; added: number; removed: number; unchanged: number };
  carriedOver: number;
  answered: number;
  priorThreads: number;
  changes: BlockChangeWire[];
}

export interface NotificationWire {
  id: string;
  projectId: string;
  threadId: string | null;
  kind: string;
  actorName: string | null;
  /** False when the name came from an unverified stranger with a link. */
  actorVerified: boolean;
  subject: string;
  readAt: number | null;
  createdAt: number;
}
